import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { AlertTriangle } from 'lucide-react';
import { PriorityDistribution as PriorityData } from '@/hooks/useDashboardMetrics';

interface PriorityDistributionProps {
  data: PriorityData[] | undefined;
  isLoading: boolean;
}

const PriorityDistribution = ({ data, isLoading }: PriorityDistributionProps) => {
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            Distribuição por Prioridade
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between animate-pulse">
            <div className="w-32 h-32 bg-gray-200 rounded-full"></div>
            <div className="flex-1 ml-6 space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) return null;

  const total = data.reduce((sum, item) => sum + item.count, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{data.priority}</p>
          <p className="text-sm text-gray-600">
            {data.count} tickets ({data.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="h-full hover:shadow-lg transition-all duration-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-orange-600">
          <AlertTriangle className="h-5 w-5" />
          Distribuição por Prioridade
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          {/* Gráfico de Pizza */}
          <div className="w-40 h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="count"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legenda e Detalhes */}
          <div className="flex-1 ml-6">
            <div className="space-y-3">
              {data.map((item, index) => (
                <div key={item.priority || `priority-${index}`} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-sm font-medium">{item.priority}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{item.count}</span>
                    <span className="text-sm text-gray-500 ml-1">({item.percentage}%)</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Total de Tickets</span>
                <span className="font-bold text-lg">{total}</span>
              </div>
            </div>

            {/* Alertas */}
            <div className="mt-4 space-y-2">
              {data.find(item => item.priority === 'Crítica' && item.count > 0) && (
                <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-xs text-red-700">
                    {data.find(item => item.priority === 'Crítica')?.count} tickets críticos requerem atenção imediata
                  </span>
                </div>
              )}
              
              {data.find(item => item.priority === 'Alta' && item.count > 10) && (
                <div className="flex items-center gap-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <span className="text-xs text-orange-700">
                    Alto volume de tickets de alta prioridade
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PriorityDistribution;
